import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@lib/redux/store';
import {
    selectCart,
    selectCartItems,
    addToCart as addToCartAction,
    removeFromCart as removeFromCartAction,
    updateCartItem as updateCartItemAction,
    resetCart,
    requestUpdateQuote,
    setCartConfig,
    applyTip,
    applyCredit,
    applyReward,
    applyHangReward,
    applyPromoCode,
    setPendingReorder,
    updateCartForNewMenu,
    setUserInfo as setUserInfoAction
} from '@lib/redux/slices/cartSlice';
import { CartLineItem } from '@lib/entities/Cart';
import { Order } from '@lib/entities';
import { Menu } from '@lib/entities/Menu';
import { UserInfo } from '@lib/entities/Address';
import { CustomRecommendation, MenuRecommendation } from '@lib/state/SaladAi';
import useFeature from './useFeature';
import useCart from './useCart';
import { push } from '@lib/analytics';
import {
    addToCart as addToCartEvent,
    removeFromCart as removeFromCartEvent
} from '@lib/analytics/google';

/**
 * Feature-flag-aware cart hook.
 * Uses Redux when payment-refactor flag is ON, otherwise uses Zustand.
 */
export const useCartRefactor = () => {
    const isRefactorEnabled = useFeature('payment-refactor', false);
    const dispatch = useDispatch();

    // Redux state
    const reduxCart = useSelector((state: RootState) => selectCart(state));
    const reduxCartItems = useSelector((state: RootState) => selectCartItems(state));

    // Zustand state
    const zustandCart = useCart();

    if (isRefactorEnabled) {
        // Redux implementation
        return {
            ...reduxCart,
            setUserInfo: (info?: UserInfo) => {
                dispatch(setUserInfoAction(info));
            },
            cartItems: reduxCartItems,
            addToCart: (item: CartLineItem) => {
                dispatch(addToCartAction(item));
                push(addToCartEvent(item));
            },
            removeFromCart: (item: CartLineItem) => {
                dispatch(removeFromCartAction(item));
                push(removeFromCartEvent(item));
            },
            updateCartItem: (item: CartLineItem) => {
                dispatch(updateCartItemAction(item));
            },
            resetCart: () => {
                dispatch(resetCart());
            },
            updateQuote: (params?: any) => {
                dispatch(requestUpdateQuote(params || {}));
            },
            setCartConfig: (config: any) => {
                dispatch(setCartConfig(config));
            },
            applyTip: (tip: any) => {
                dispatch(applyTip(tip));
            },
            applyCredit: (credit: any) => {
                dispatch(applyCredit(credit));
            },
            applyReward: (reward: any) => {
                dispatch(applyReward(reward));
            },
            applyHangReward: (reward: any) => {
                dispatch(applyHangReward(reward));
            },
            applyPromoCode: (code: string) => {
                dispatch(applyPromoCode(code));
            },
            resetPromoCode: () => {
                dispatch(setCartConfig({ promoCode: undefined }));
            },
            isItemInCart: (itemId: string) => {
                return reduxCartItems.some((item) => item.id === itemId);
            },
            setPendingReorder: (order?: Order) => {
                dispatch(setPendingReorder(order));
            },
            updateCartForNewMenu: (menu: Menu) => {
                dispatch(updateCartForNewMenu(menu));
            },
            addAiRecommendedItem: (item: MenuRecommendation | CustomRecommendation) => {
                const isCustom = 'included_saladai_ingredients' in item;
                const ingredients = isCustom
                    ? item.included_saladai_ingredients
                    : item.includedIngredients;

                const orderItem = {
                    id: item.id,
                    menuitem_id: item.id,
                    quantity: 1,
                    name: item.name,
                    price: item.price,
                    description: item.description,
                    customizations: isCustom
                        ? ingredients.map((i) => ({ ...i, ingredient_id: i.id, quantity: 1 }))
                        : [],
                    ingredients: !isCustom ? ingredients : [],
                    isCustom: isCustom,
                    saladai_llm_out_id: item.saladai_llm_out_id
                };

                const order = {
                    items: [orderItem],
                    total: item.price,
                    subtotal: item.price,
                    createdAt: new Date().toISOString(),
                    status: 'pending',
                    specialInstructions: ''
                } as unknown as Order;

                dispatch(setPendingReorder(order));
            },
            goToCheckoutSignal: reduxCart.goToCheckoutSignal
        };
    }

    // Zustand implementation (legacy)
    return zustandCart;
};

export default useCartRefactor;
